import { DataTable, type Column, type Pagination, type RowSelection } from '@platform/design-system';
import type { ReactNode } from 'react';
import { LoadFailed } from './LoadFailed.tsx';
import { usePageContext } from './usePageContext.ts';

export type ResourceQuery = {
  readonly isError: boolean;
  readonly isPending: boolean;
  readonly error: unknown;
  readonly refetch: () => unknown;
};

export type ResourceTableProps<Row> = {
  query: ResourceQuery;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  emptyTitle: string;
  emptyDescription?: string | undefined;
  onRowClick?: ((row: Row) => void) | undefined;
  selection?: RowSelection<Row> | undefined;
  activeKey?: string | null | undefined;
  highlightKey?: string | null | undefined;
  toolbar?: ReactNode;
  footer?: ReactNode;
  bare?: boolean;
  pagination?: Pagination | undefined;
};

export const ResourceTable = <Row,>({
  query,
  columns,
  rows,
  rowKey,
  emptyTitle,
  emptyDescription,
  onRowClick,
  selection,
  activeKey,
  highlightKey,
  toolbar,
  footer,
  bare = false,
  pagination,
}: ResourceTableProps<Row>) => {
  const { describeError, tableLabels } = usePageContext();

  if (query.isError)
    return (
      <LoadFailed
        description={describeError(query.error)}
        onRetry={() => {
          query.refetch();
        }}
      />
    );

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={rowKey}
      loading={query.isPending}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      labels={tableLabels}
      onRowClick={onRowClick}
      selection={selection}
      activeKey={activeKey}
      highlightKey={highlightKey}
      toolbar={toolbar}
      footer={footer}
      bare={bare}
      pagination={pagination}
    />
  );
};
